import React, { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import {
  GridColDef,
  GridFilterModel,
  GridRenderCellParams,
  GridSortModel,
} from "@mui/x-data-grid";
import { Checkbox } from "@mui/material";
import {
  FetchPaginatedApiModel,
  WithData,
  WithFabObj,
  WithRowOperations,
} from "../../../models/shared/CommonModels";
import { SortingModel } from "../../../models/shared/SortingModel";
import { castToBackendSort } from "../../../utils/sorting";
import DeleteDialog from "../../shared/DeleteDialog";
import FilteredDataTable from "../../shared/FilteredDataTable";
import { BeneCollezioneModel } from "../../../models/api/response/bene/BeneCollezioneModel";
import { deleteCollezione, getCollezione, updateCollezione } from "../../../api/bene/collezione";
import BeneCollezioneFormComponent from "./BeneCollezioneFormComponent";
import { fetchCollezioneBeneSelectQueryKey, fetchBeniQueryKey } from "../../../utils/QueryClient";

interface BeneCollezioneTableComponentProps extends WithFabObj {
  enableCreate: boolean;
}

const BeneCollezioneTableComponent: React.FC<
  BeneCollezioneTableComponentProps
> = ({ fabVisible, alterFabVisibility, enableCreate }) => {
  const queryClient = useQueryClient();

  const [editFormOpen, setEditFormOpen] = useState<boolean>(false);
  const toggleEditFormOpen = () => setEditFormOpen(!editFormOpen);

  const [deleteAlertOpen, setDeleteAlertOpen] = useState(false);
  const toggleDeleteAlert = () => setDeleteAlertOpen(!deleteAlertOpen);

  const [paginationModel, setPaginationModel] = React.useState({
    page: 0,
    pageSize: 5,
  });
  const [sortBy, setSortBy] = useState("");
  const [sortDirection, setSortDirection] = useState("");

  const [collections, setCollections] = useState<
    FetchPaginatedApiModel<BeneCollezioneModel[]>
  >(undefined as any);
  const [selectedRowElement, setSelectedRowElement] =
    useState<BeneCollezioneModel>();

  const [filters, setFilters] = useState<GridFilterModel | undefined>();
  const [sortModel, setSortModel] = useState<GridSortModel>([]);

  const [errorMessage, setErrorMessage] = useState("");
  const hideError = () => setErrorMessage("");

  // query
  useQuery(
    [
      fetchCollezioneBeneSelectQueryKey,
      paginationModel.page,
      filters,
      sortBy,
      sortDirection,
    ],
    () => {
      const sor = {} as SortingModel;
      sor.field = sortBy || "id";
      sor.direction = sortDirection || "DESC";
      return getCollezione(0, paginationModel.page, sor, filters);
    },
    {
      keepPreviousData: true,
      onSuccess: (getResponse) => {
        setCollections(getResponse.data);
      },
      refetchOnWindowFocus: false,
    }
  );

  const deleteMutation = useMutation({
    mutationFn: (x: WithData) => deleteCollezione(x.data),
    onSuccess: () => {
      queryClient.invalidateQueries(fetchCollezioneBeneSelectQueryKey);
      queryClient.invalidateQueries(fetchBeniQueryKey);
    },
    onError: (e: any) => {
      if (e.response.data.message !== undefined)
        setErrorMessage(e.response.data.message);
      else
        setErrorMessage(
          "Errore non previsto, riprovare o richiedere assistenza"
        );
    },
  });

  const updateDefaultMutation = useMutation({
    mutationFn: (params: { id: number; record: BeneCollezioneModel }) => {
      return updateCollezione({
        recordID: params.id,
        data: {
          collezionebene: {
            nome: params.record.nome,
            descrizione: params.record.descrizione,
            default: params.record.default === 1 ? 0 : 1,
          },
        },
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries(fetchCollezioneBeneSelectQueryKey);
    },
  });

  const editOperations = (
    record: GridRenderCellParams<any, BeneCollezioneModel>
  ) => {
    setSelectedRowElement(record.value);
    toggleEditFormOpen();
  };

  const deleteOperations = (
    record: GridRenderCellParams<any, BeneCollezioneModel>
  ) => {
    setSelectedRowElement(record.value);
    toggleDeleteAlert();
  };

  const columns: GridColDef[] = [
    {
      field: "id",
      headerName: "ID Collezione Bene",
      flex: 1,
    },
    {
      field: "nome",
      headerName: "Nome",
      flex: 1,
    },
    {
      field: "descrizione",
      headerName: "Descrizione",
      sortable: false,
      flex: 1,
    },
    {
      field: "default",
      headerName: "Default",
      flex: 0.5,
      renderCell: (params: GridRenderCellParams) => (
        <Checkbox
          checked={params.row.default === 1}
          onChange={() => updateDefaultMutation.mutate({ id: params.row.id, record: params.row })}
        />
      ),
    },
  ];

  return (
    <>
      <FilteredDataTable<BeneCollezioneModel, WithRowOperations>
        loading={collections === undefined}
        editOperations={editOperations}
        deleteOperations={deleteOperations}
        rows={
          collections?.data !== undefined
            ? collections.data.map((d) => {
                return {
                  id: d.id,
                  nome: d.nome,
                  descrizione: d.descrizione,
                  default: d.default,
                  actions: d,
                  delete_action: d,
                  hasEdit: true,
                  hasDelete: true,
                };
              })
            : []
        }
        rowCount={collections?.count}
        onPaginationModelChange={setPaginationModel}
        columns={columns}
        errorMessage={errorMessage}
        hideError={hideError}
        filters={filters}
        sortModel={sortModel}
        onFilterChange={(newFilterModel) => setFilters(newFilterModel)}
        onSortChange={(newSortModel) => {
          newSortModel.forEach((item) => {
            if (item !== undefined) {
              setSortBy(item.field);
              setSortDirection(castToBackendSort(item.sort));
            }
          });

          setSortModel(newSortModel);
        }}
      />

      {/* Create */}
      {
        <BeneCollezioneFormComponent
          initialData={{} as BeneCollezioneModel}
          open={enableCreate ? !fabVisible : false}
          toggleOpen={() => {}}
          onClose={() => alterFabVisibility(true)}
        />
      }

      {/* Edit */}
      {selectedRowElement !== undefined && (
        <BeneCollezioneFormComponent
          initialData={selectedRowElement}
          open={editFormOpen}
          toggleOpen={() => {
            toggleEditFormOpen();
          }}
          onClose={() => {
            setSelectedRowElement(undefined);
            toggleEditFormOpen();
          }}
          editEnabled={true}
        />
      )}

      {/* Delete */}
      <DeleteDialog
        open={deleteAlertOpen}
        onClose={toggleDeleteAlert}
        identifier={selectedRowElement?.id}
        onPositive={() => {
          toggleDeleteAlert();
          const x = {} as WithData;
          x.data = selectedRowElement?.id;
          deleteMutation.mutate(x);
        }}
        onNegative={toggleDeleteAlert}
      />
    </>
  );
};

export default BeneCollezioneTableComponent;
